import { sortTransactionsByDateAndId } from '../../formatter';
import { convertDatetimeStringToDayTimestamp, selectDate } from '@algoan/dashboard/utils';
import {
  BankStatement,
  BankStatementRow,
  FilterStatement,
  TransactionsByCashFlowCategory,
  TransactionsByCashFlowType,
  SortStatement,
  Transaction,
  TransactionDates,
} from '@algoan/dashboard/model';

export const getTransactionIdByCashFlowCategory = (transactions: Transaction[]): TransactionsByCashFlowCategory => {
  return transactions.reduce<TransactionsByCashFlowCategory>((transactionIdByType, transaction) => {
    const category = transaction.enrichments?.category;
    if (category && !(category in transactionIdByType)) {
      transactionIdByType[category] = [];
    }

    if (category) {
      transactionIdByType[category] = transactionIdByType[category]?.concat(transaction.id);
    }

    return transactionIdByType;
  }, {});
};

export const getTransactionIdByCashFlowType = (transactions: Transaction[]): TransactionsByCashFlowType => {
  return transactions.reduce<TransactionsByCashFlowType>((transactionIdByType, transaction) => {
    const type = transaction.enrichments?.type;
    if (type && !(type in transactionIdByType)) {
      transactionIdByType[type] = [];
    }

    if (type) {
      transactionIdByType[type] = transactionIdByType[type]?.concat(transaction.id);
    }

    return transactionIdByType;
  }, {});
};

export const filterBankStatements = (
  bankStatements: BankStatement[],
  transactionsByCashFlowCategory: TransactionsByCashFlowCategory,
  transactionsByCashFlowType: TransactionsByCashFlowType,
  filters: FilterStatement,
): BankStatement[] =>
  bankStatements.filter((bankStatement) => {
    const selectedByAccountFilter = filters.accounts.length === 0 || filters.accounts.includes(bankStatement.accountId);

    const selectedByCategoryFilter =
      filters.cashFlowsCategory.length === 0 ||
      filters.cashFlowsCategory.some(
        (cashFlowsCategory) => !!transactionsByCashFlowCategory[cashFlowsCategory]?.includes(bankStatement.id),
      );

    const selectedByTypeFilter =
      filters.cashFlowsTypes.length === 0 ||
      filters.cashFlowsTypes.some(
        (cashFlowType) => !!transactionsByCashFlowType[cashFlowType]?.includes(bankStatement.id),
      );

    const transactionDaysFilter = getTransactionDayFilter({
      transactionDays: filters.transactionDays,
      dates: bankStatement.dates,
    });

    return selectedByAccountFilter && selectedByCategoryFilter && selectedByTypeFilter && transactionDaysFilter;
  });

const isCommentDefined = (comment?: string): comment is string => comment !== undefined && comment.length > 0;

export const sortBankStatements = (
  bankStatements: BankStatementRow[],
  { sortBy, sortDirection }: SortStatement,
): BankStatementRow[] =>
  bankStatements.slice().sort((statementA, statementB) => {
    let firstStatement = statementA;
    let secondStatement = statementB;

    if (sortDirection === 'DESC') {
      firstStatement = statementB;
      secondStatement = statementA;
    }

    if (['accountId', 'algoanCategory', 'algoanType'].includes(sortBy)) {
      const firstValue = firstStatement[sortBy as keyof BankStatementRow] ?? '';
      const secondValue = secondStatement[sortBy as keyof BankStatementRow] ?? '';
      if (firstValue < secondValue) {
        return -1;
      }
      if (firstValue > secondValue) {
        return 1;
      }

      return 0;
    }

    if (sortBy === 'comment') {
      if (isCommentDefined(firstStatement.comment) && isCommentDefined(secondStatement.comment)) {
        if (firstStatement.comment < secondStatement.comment) {
          return -1;
        } else if (firstStatement.comment > secondStatement.comment) {
          return 1;
        }
      } else if (!isCommentDefined(firstStatement.comment)) {
        return 1;
      } else if (!isCommentDefined(secondStatement.comment)) {
        return -1;
      }
      return 0;
    }

    if (sortBy === 'date') {
      return sortTransactionsByDateAndId(secondStatement, firstStatement);
    }

    return firstStatement.amount - secondStatement.amount;
  });

interface GetTransactionDayFilterProps {
  transactionDays?: number[];
  dates: TransactionDates;
}

const getTransactionDayFilter = ({ transactionDays, dates }: GetTransactionDayFilterProps): boolean => {
  if (transactionDays === undefined) return true;
  return transactionDays.length === 1
    ? transactionDays[0] === convertDatetimeStringToDayTimestamp(selectDate(dates))
    : transactionDays[0] <= convertDatetimeStringToDayTimestamp(selectDate(dates)) &&
        convertDatetimeStringToDayTimestamp(selectDate(dates)) <= transactionDays[1];
};
